<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div>
    <salary-slip
      v-if="showSaveModal"
      :salary-details="salaryDetails"
      :organization="organization"
      @close="onSaveModalClose"
    ></salary-slip>
    <div
      class="orangehrm-horizontal-padding orangehrm-vertical-padding paddind-bottom-none"
    >
      <oxd-form @submit-valid="filterItems">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-text class="orangehrm-sub-title" tag="h6"
                >Monthly Salary</oxd-text
              >
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-grid :cols="2" class="orangehrm-full-width-grid">
                <oxd-grid-item>
                  <oxd-input-field
                    v-model="filters.years"
                    type="select"
                    :options="years"
                    @change="onYearOnChange"
                  />
                </oxd-grid-item>
                <oxd-grid-item>
                  <div class="go-button">
                    <submit-button label="Get Payslips" />
                  </div>
                </oxd-grid-item>
              </oxd-grid>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
      </oxd-form>
    </div>
    <table-header
      :selected="checkedItems.length"
      :total="total"
      :loading="isLoading"
    ></table-header>
    <div class="orangehrm-container">
      <oxd-card-table
        :headers="tableHeaders"
        :items="items?.data"
        :disabled="isDisabled"
        :clickable="false"
        :loading="isLoading"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
    <div class="orangehrm-bottom-container">
      <oxd-pagination
        v-if="showPaginator"
        v-model:current="currentPage"
        :length="pages"
      />
    </div>
  </div>
</template>

<script>
import {computed, ref} from 'vue';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import {APIService} from '@ohrm/core/util/services/api.service';
import SalarySlip from '@/orangehrmPimPlugin/components/SalarySlip';
import {formatDate} from '@ohrm/core/util/helper/datefns';
const currentYear = new Date().getFullYear().toString();
const defaultFilters = {
  years: {id: currentYear, label: currentYear},
};
const salaryNormalizer = (data) => {
  return data.map((item) => {
    return {
      id: item.id,
      empFullName: item.empFullName,
      designation: item.designation,
      joiningDate: formatDate(new Date(item.joiningDate?.date), 'dd MMM yyyy'),
      panNumber: item.panNumber,
      year: item.year,
      month: item.month,
      monthYear: item.month + '-' + item.year,
      paymentMode: item.paymentMode,
      bank: item.bank,
      bankIfsc: item.bankIfsc,
      accountNumber: item.accountNumber,
      totalDays: item.totalDays,
      totalLeaves: item.totalLeaves,
      leaveAdjusted: item.leaveAdjusted,
      totalPayablesDays: item.totalPayablesDays,
      monthlySalary: item.monthlySalary,
      basicSalary: item.basicSalary,
      hra: item.hra,
      conveyanceAllowance: item.conveyanceAllowance,
      communicationAllowance: item.communicationAllowance,
      specialAllowance: item.specialAllowance,
      totalEarnings: item.totalEarnings,
      providentFund: item.providentFund,
      professionalTax: item.professionalTax,
      tds: item.tds,
      totalDeductions: item.totalDeductions,
      netPay: item.netPay,
      currency: item.currencyType?.name,
    };
  });
};

export default {
  components: {
    'salary-slip': SalarySlip,
  },
  props: {
    organization: {
      type: Object,
      default: () => ({}),
    },
    empNumber: {
      type: String,
      required: true,
    },
    currencies: {
      type: Array,
      default: () => [],
    },
    years: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const serializedFilters = computed(() => {
      return {
        year: filters.value.years?.id,
      };
    });
    const filters = ref({...defaultFilters});
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/employees/${props.empNumber}/monthly-salary`,
    );

    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer: salaryNormalizer,
      toastNoRecords: false,
    });
    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      filters,
    };
  },

  data() {
    return {
      headers: [
        {
          name: 'monthYear',
          title: 'Month & Year',
          style: {flex: 1},
        },
        {
          name: 'totalEarnings',
          title: 'Gross Earnings',
          style: {flex: 1},
        },
        {
          name: 'totalDeductions',
          title: 'Total Deductions',
          style: {flex: 1},
        },
        {
          name: 'netPay',
          title: 'Net Earnings',
          style: {flex: 1},
        },
      ],
      checkedItems: [],
      showSaveModal: false,
      showEditModal: false,
      salaryDetails: null,
    };
  },

  computed: {
    isDisabled() {
      return this.showSaveModal || this.showEditModal;
    },
    tableHeaders() {
      const headerActions = {
        name: 'actions',
        slot: 'action',
        title: this.$t('general.actions'),
        style: {flex: 1},
        cellType: 'oxd-table-cell-actions',
        cellConfig: {},
      };
      headerActions.cellConfig.edit = {
        onClick: this.onClickEdit,
        props: {
          name: 'file-text-fill',
        },
      };
      return Object.keys(headerActions.cellConfig).length > 0
        ? this.headers.concat([headerActions])
        : this.headers;
    },
  },
  methods: {
    onClickEdit(item) {
      this.showEditModal = false;
      this.salaryDetails = item;
      this.showSaveModal = true;
    },
    onSaveModalClose() {
      this.showSaveModal = false;
      this.salaryDetails = null;
    },
    onEditModalClose() {
      this.showEditModal = false;
      this.salaryDetails = null;
    },
    async filterItems() {
      await this.execQuery();
    },
  },
};
</script>
<style lang="scss" scoped>
.paddind-bottom-none {
  padding-bottom: unset;
}
.go-button {
  margin-top: 12px;
  margin-left: -10px;
}
</style>
